import usePageViewState from '../../components/usePageViewState';
import RowActionsMenu from '../../components/RowActionsMenu';
import React, { useEffect, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../../auth/AuthProvider';
import { AccessDenied } from '../../routes/AuthRoutes';
import { Check, CircleCheck, Eye, KeyRound, ListChecks, Pencil, Plus, RotateCcw, Search, Trash2, UsersRound } from 'lucide-react';
import { createUser, deleteUser, listRoles, listUsers, resetUserPassword, updateUser } from './userService';
import FilterMenu from '../../components/FilterMenu';
import { CreateUserModal } from './CreateUserPage';
import { usePermissions } from '../../auth/permissions';
import UserEditModal from './UserEditModal';
import Toast from '../../components/Toast';
import TableScroll from '../../components/TableScroll';
import '../warehouses/warehouses.css';
import './users.css';

export default function UsersPage() {
  const { canWrite } = usePermissions();
  const writable = canWrite('Users');
  const permissionAdmin = useAuth()?.user?.role === 'Admin';
  const navigate = useNavigate();
  const location = useLocation();
  const [toast, setToast] = useState(() => location.state?.userCreated ? { tone: 'success', message: 'User created successfully.' } : null);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [query, setQuery] = useState('');
  const [roles, setRoles] = useState([]);
  const [roleId, setRoleId] = useState('');
  const [status, setStatus] = useState('');
  const [openFilter, setOpenFilter] = useState(null);
  useEffect(() => {
    let active = true;
    listRoles().then(items => { if (active) setRoles(items); }).catch(err => {
      if (active) { if (err.status === 403) setError(err); setToast({ tone: 'error', message: err.message }); }
    });
    return () => { active = false; };
  }, []);
  const [attempt, setAttempt] = useState(0);
  const [editing, setEditing] = usePageViewState('editing', null);
  const [creating, setCreating] = usePageViewState('creating', () => Boolean(location.state?.openCreateUser));
  const [deleting, setDeleting] = useState(null);
  const [resetting, setResetting] = useState(null);
  const [saving, setSaving] = useState(false);
  const busy = useRef(false);
  const returnFocus = useRef(null);
  useEffect(() => {
    if (!deleting && !resetting) return undefined;
    function key(event) { if (event.key === 'Escape' && !busy.current) { setDeleting(null); setResetting(null); returnFocus.current?.focus(); } }
    document.addEventListener('keydown', key);
    return () => document.removeEventListener('keydown', key);
  }, [deleting, resetting]);
  useEffect(() => {
    let active = true;
    setLoading(true); setError(null);
    listUsers().then(rows => { if (active) setUsers(rows); }).catch(err => {
      if (active) { setError(err); setToast({ tone: 'error', message: err.message }); }
    }).finally(() => { if (active) setLoading(false); });
    return () => { active = false; };
  }, [attempt]);
  useEffect(() => {
    if (!toast || toast.tone !== 'success') return undefined;
    const timer = setTimeout(() => setToast(null), 5000);
    return () => clearTimeout(timer);
  }, [toast]);
  function closeEdit() { setEditing(null); returnFocus.current?.focus(); }
  function closeCreate() { setCreating(false); returnFocus.current?.focus(); }
  function closeDelete() { if (!busy.current) { setDeleting(null); returnFocus.current?.focus(); } }
  function closeReset() { if (!busy.current) { setResetting(null); returnFocus.current?.focus(); } }
  async function confirmReset() {
    if (busy.current || !resetting) return;
    busy.current = true; setSaving(true);
    try {
      await resetUserPassword(resetting.id);
      setResetting(null); returnFocus.current?.focus();
      setToast({ tone: 'success', message: 'Password reset successfully to Pass@123.' });
    } catch (err) { handleFailure(err); }
    finally { setResetting(null); busy.current = false; setSaving(false); returnFocus.current?.focus(); }
  }
  function handleFailure(err) {
    if (err.status === 403) setError(err);
    setToast({ tone: 'error', message: err.message });
  }
  async function saveUser(id, payload) {
    try {
      const updated = await updateUser(id, payload);
      setUsers(current => current.map(item => item.id === id ? updated : item));
      setToast({ tone: 'success', message: 'User updated successfully.' });
    } catch (err) {
      if (err.status === 403 || err.status === 404) {
        closeEdit();
        setToast({ tone: 'error', message: "You don't have permission to edit this user." });
        return;
      }
      handleFailure(err); throw err;
    }
  }
  async function addUser(payload) {
    const created = await createUser(payload);
    setUsers(current => [created, ...current]);
    setToast({ tone: 'success', message: 'User created successfully.' });
  }
  async function confirmDelete() {
    if (busy.current || !deleting) return;
    busy.current = true; setSaving(true);
    try {
      await deleteUser(deleting.id);
      setUsers(current => current.filter(item => item.id !== deleting.id));
      setDeleting(null);
      setToast({ tone: 'success', message: 'User deleted successfully.' });
    } catch (err) { handleFailure(err); }
    finally { setDeleting(null); busy.current = false; setSaving(false); returnFocus.current?.focus(); }
  }
  if (error?.status === 403) return <AccessDenied />;
  const term = query.trim().toLowerCase();
  const rows = users.filter(user => (!roleId || String(user.role_id) === roleId)
    && (!status || user.is_active === (status === 'active'))
    && [user.name, user.email, user.mobile, user.role].some(value => String(value || '').toLowerCase().includes(term)));
  return <section className="wh-page wh-list-page users-page">
    <Toast toast={toast} onClose={() => setToast(null)} />
    <header className="wh-title"><div><h1>Users</h1><p></p></div>
      {writable && <button className="wh-add" type="button" onClick={event => { returnFocus.current = event.currentTarget; setCreating(true); }}><Plus size={14} />Add User</button>}
    </header>
    <div className="wh-panel">
      <div className="wh-filters"><label className="wh-search"><Search size={15} /><input type="search" aria-label="Search users" placeholder="Search users..." value={query} onChange={event => { setQuery(event.target.value); }} /></label>
        <FilterMenu ariaLabel="Role filter" label="Role" icon={<UsersRound size={14} strokeWidth={1.8} />} value={roles.find(role => String(role.id) === roleId)?.name || 'All Roles'} open={openFilter === 'role'} onToggle={() => { setOpenFilter(current => current === 'role' ? null : 'role'); }} onClose={() => setOpenFilter(null)}>
          <div className="wh-menu-list">{[{ id: '', name: 'All Roles' }, ...roles].map(role => <button key={role.id} type="button" role="option" aria-selected={roleId === String(role.id)} className={`wh-menu-item${roleId === String(role.id) ? ' selected' : ''}`} onClick={() => { setRoleId(String(role.id)); setOpenFilter(null); }}><span>{role.name}</span>{roleId === String(role.id) && <Check size={14} />}</button>)}</div>
        </FilterMenu>
        <FilterMenu ariaLabel="Status filter" label="Status" icon={<CircleCheck size={14} strokeWidth={1.8} />} value={status ? status === 'active' ? 'Active' : 'Inactive' : 'All Status'} open={openFilter === 'status'} onToggle={() => { setOpenFilter(current => current === 'status' ? null : 'status'); }} onClose={() => setOpenFilter(null)}>
          <div className="wh-menu-list">{[['', 'All Status'], ['active', 'Active'], ['inactive', 'Inactive']].map(([value, label]) => <button key={value} type="button" role="option" aria-selected={status === value} className={`wh-menu-item${status === value ? ' selected' : ''}`} onClick={() => { setStatus(value); setOpenFilter(null); }}><span>{label}</span>{status === value && <Check size={14} />}</button>)}</div>
        </FilterMenu>
        <button className="wh-filter-btn" type="button" onClick={() => { setQuery(''); setRoleId(''); setStatus(''); setOpenFilter(null); }}><RotateCcw size={14} strokeWidth={1.7} />Reset</button>
      </div>
      <TableScroll><table className="wh-table"><thead><tr>{(writable || permissionAdmin) && <th className="wh-actions-heading" aria-label="Actions" title="Actions"><ListChecks size={16} aria-hidden="true" /></th>}<th>Name</th><th>Email</th><th>Mobile</th><th>Role</th><th>Status</th></tr></thead>
        <tbody>{loading || error || !rows.length ? <tr><td colSpan={writable || permissionAdmin ? 6 : 5} className="users-empty">
          {loading ? 'Loading users...' : error ? <><span role="alert">{error.message}</span> <button type="button" className="wh-form-cancel" onClick={() => setAttempt(value => value + 1)}>Retry</button></> : 'No users found.'}
        </td></tr> : rows.map(user => <tr key={user.id}>{(writable || permissionAdmin) && <td className="wh-actions-cell">
          <RowActionsMenu label={`Actions for ${user.name}`} actions={[
            ...(permissionAdmin ? [{ label: 'Permissions', icon: Eye, onClick: () => navigate(`/users/${user.id}/permissions`) }] : []),
            ...(writable ? [
              { label: 'Edit', icon: Pencil, onClick: trigger => { returnFocus.current = trigger; setEditing(user); } },
              { label: 'Reset Password', icon: KeyRound, onClick: trigger => { returnFocus.current = trigger; setResetting(user); } },
              { label: 'Delete', icon: Trash2, danger: true, onClick: trigger => { returnFocus.current = trigger; setDeleting(user); } },
            ] : []),
          ]} />
        </td>}<td>{user.name}</td><td>{user.email}</td><td>{user.mobile || '—'}</td><td>{user.role}</td><td>{user.is_active ? 'Active' : 'Inactive'}</td></tr>)}</tbody>
      </table></TableScroll>
    </div>
    {writable && resetting && <div className="wh-modal-backdrop" onMouseDown={event => { if (event.target === event.currentTarget) closeReset(); }}>
      <div className="wh-modal wh-modal-confirm" role="dialog" aria-modal="true" aria-labelledby="user-reset-title">
        <h2 id="user-reset-title">Reset Password?</h2><p className="wh-modal-copy">Reset this user's password to Pass@123?</p>
        <div className="wh-delete-target"><strong>{resetting.name}</strong><span>{resetting.email}</span></div>
        <div className="wh-modal-actions"><button type="button" className="wh-btn-ghost" autoFocus disabled={saving} onClick={closeReset}>Cancel</button><button type="button" className="wh-form-save" disabled={saving} onClick={confirmReset}>{saving ? 'Resetting...' : 'Reset Password'}</button></div>
      </div>
    </div>}
    {writable && deleting && <div className="wh-modal-backdrop" onMouseDown={event => { if (event.target === event.currentTarget) closeDelete(); }}>
      <div className="wh-modal wh-modal-confirm" role="dialog" aria-modal="true" aria-labelledby="user-delete-title">
        <h2 id="user-delete-title">Delete User?</h2><p className="wh-modal-copy">Are you sure you want to delete this user?</p>
        <div className="wh-delete-target"><strong>{deleting.name}</strong><span>{deleting.email}</span></div>
        <div className="wh-modal-actions"><button type="button" className="wh-btn-ghost" autoFocus disabled={saving} onClick={closeDelete}>Cancel</button><button type="button" className="wh-btn-danger" disabled={saving} onClick={confirmDelete}>{saving ? 'Deleting...' : 'Delete'}</button></div>
      </div>
    </div>}
    {writable && creating && <CreateUserModal onClose={closeCreate} onSubmit={addUser} />}
    {writable && editing && <UserEditModal user={editing} onClose={closeEdit} onSubmit={saveUser} />}
  </section>;
}
